import * as PopoverPrimitive from "@radix-ui/react-popover"
import { Check, ChevronDown, Loader2, Search, X } from "lucide-react"
import * as React from "react"

import { type FieldControlProps, useFieldProps } from "@/components/ui/form-field"
import { cn } from "@/lib/utils"

/*
   For a set too large to scan — the 79 rules, every user in the console,
   every client on the account. The person usually knows what they want
   before they open it, so the search field is focused on open and the list
   filters as they type.

   Built on Radix Popover with the listbox implemented here rather than on
   cmdk, for one reason: the rule picker needs a `scope` — when a reviewer
   draws a box on the label, the section the box landed in narrows the list
   to the seven rules that could apply, with an explicit, visible way back
   to all 79. That is a first-class state, not a pre-filtered query string,
   and it needs to survive typing.

   Keyboard: ↑↓ move, Enter selects, Esc closes, Home/End jump, and
   aria-activedescendant keeps a screen reader in step without moving DOM
   focus off the input.
*/

export interface ComboboxOption {
  value: string
  /** The searchable, displayable label. */
  label: string
  /** Rendered under the label. The rule text, the person's email. */
  description?: string
  /** Rendered flush-right, muted and mono. A citation, a role, a count. */
  meta?: string
  /** Group heading this option sits under. */
  group?: string
  /** Extra terms that should match this option but aren't shown. */
  keywords?: string[]
  disabled?: boolean
  icon?: React.ReactNode
}

export interface ComboboxProps {
  options: ComboboxOption[]
  value?: string | null
  onValueChange?: (value: string | null) => void
  /** Trigger text when nothing is chosen. */
  placeholder?: string
  /** Search field placeholder. Say what can be searched, not "Search…". */
  searchPlaceholder?: string
  /**
   * Narrows the list to a subset, with a visible chip and a way back.
   * The rule picker's reason for existing: a box drawn in
   * `precautionary_statements` scopes to the rules for that section.
   */
  scope?: {
    /** Shown in the chip: "precautionary statements". */
    label: string
    /** Option values inside the scope. */
    values: string[]
  } | null
  onClearScope?: () => void
  /** Async source is still loading. */
  loading?: boolean
  /** Message when the query matches nothing. */
  emptyMessage?: React.ReactNode
  /** Rendered at the foot of the list — "Add a new client", say. */
  footer?: React.ReactNode
  disabled?: boolean
  invalid?: boolean
  size?: "sm" | "md" | "lg"
  /** Render the trigger value in mono. For rule ids and reg numbers. */
  mono?: boolean
  className?: string
  id?: string
  "aria-describedby"?: string
}

const SIZE = {
  sm: "h-7.5 px-2.5 text-body-sm",
  md: "h-9 px-3 text-body",
  lg: "h-10.5 px-3.5 text-body",
} as const

export function Combobox({
  options,
  value = null,
  onValueChange,
  placeholder = "Choose one",
  searchPlaceholder = "Search",
  scope = null,
  onClearScope,
  loading = false,
  emptyMessage,
  footer,
  disabled = false,
  invalid,
  size = "md",
  mono = false,
  className,
  ...rest
}: ComboboxProps) {
  const fieldProps = useFieldProps<FieldControlProps>(rest)
  const isInvalid =
    invalid ?? (fieldProps["aria-invalid"] === true || fieldProps["aria-invalid"] === "true")

  const [open, setOpen] = React.useState(false)
  const [query, setQuery] = React.useState("")
  const [active, setActive] = React.useState(0)

  const listId = React.useId()
  const inputRef = React.useRef<HTMLInputElement>(null)
  const listRef = React.useRef<HTMLDivElement>(null)

  const selected = options.find((o) => o.value === value) ?? null

  /* Scope first, then query. Scope survives typing — a reviewer narrowing
     to a section and then searching within it is the common path. */
  const inScope = React.useMemo(
    () => (scope ? options.filter((o) => scope.values.includes(o.value)) : options),
    [options, scope],
  )

  const filtered = React.useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return inScope
    return inScope.filter((o) => {
      const hay = [o.label, o.description, o.meta, ...(o.keywords ?? [])]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
      return hay.includes(q)
    })
  }, [inScope, query])

  /* Group while preserving the order options were given in. */
  const groups = React.useMemo(() => {
    const map = new Map<string, ComboboxOption[]>()
    for (const o of filtered) {
      const key = o.group ?? ""
      if (!map.has(key)) map.set(key, [])
      map.get(key)!.push(o)
    }
    return [...map.entries()]
  }, [filtered])

  const flat = filtered.filter((o) => !o.disabled)

  React.useEffect(() => setActive(0), [query, scope])

  React.useEffect(() => {
    if (!open) return
    // scroll the active row into view without moving focus off the input
    const el = listRef.current?.querySelector<HTMLElement>(`[data-index="${active}"]`)
    el?.scrollIntoView({ block: "nearest" })
  }, [active, open])

  const commit = (opt: ComboboxOption) => {
    onValueChange?.(opt.value === value ? null : opt.value)
    setOpen(false)
    setQuery("")
  }

  const onKeyDown = (e: React.KeyboardEvent) => {
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault()
        setActive((i) => Math.min(i + 1, flat.length - 1))
        break
      case "ArrowUp":
        e.preventDefault()
        setActive((i) => Math.max(i - 1, 0))
        break
      case "Home":
        e.preventDefault()
        setActive(0)
        break
      case "End":
        e.preventDefault()
        setActive(flat.length - 1)
        break
      case "Enter": {
        e.preventDefault()
        const opt = flat[active]
        if (opt) commit(opt)
        break
      }
      case "Escape":
        e.preventDefault()
        setOpen(false)
        break
      // Backspace on an empty query steps back out of the scope
      case "Backspace":
        if (!query && scope && onClearScope) {
          e.preventDefault()
          onClearScope()
        }
        break
    }
  }

  let index = -1

  return (
    <PopoverPrimitive.Root open={open} onOpenChange={setOpen}>
      <PopoverPrimitive.Trigger asChild>
        <button
          type="button"
          /* The button is a disclosure that opens a listbox popup. The actual
             combobox (role, aria-activedescendant, aria-autocomplete) is the
             input inside the popover — that's where typeahead focus lives.
             See WAI-ARIA APG "Combobox with Both List and Inline Autocomplete". */
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-controls={open ? listId : undefined}
          aria-invalid={isInvalid || undefined}
          disabled={disabled}
          id={fieldProps.id}
          aria-describedby={fieldProps["aria-describedby"]}
          className={cn(
            "flex w-full items-center justify-between gap-2 rounded-md border bg-surface text-left",
            "transition-colors duration-120 ease-out",
            "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2",
            "focus-visible:outline-[color:var(--brand)]",
            "disabled:cursor-not-allowed disabled:bg-surface-sunken disabled:text-text-disabled",
            SIZE[size],
            isInvalid ? "border-err bg-err-tint/40" : "border-border hover:border-border-strong",
            className,
          )}
        >
          <span
            className={cn(
              "min-w-0 flex-1 truncate",
              selected ? "text-text" : "text-text-disabled",
              mono && selected && "font-id tabular-nums",
            )}
          >
            {selected ? selected.label : placeholder}
          </span>
          <ChevronDown className="size-4 shrink-0 text-text-3" aria-hidden="true" />
        </button>
      </PopoverPrimitive.Trigger>

      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          align="start"
          sideOffset={6}
          onOpenAutoFocus={(e) => {
            e.preventDefault()
            inputRef.current?.focus()
          }}
          className={cn(
            "z-modal w-[var(--radix-popover-trigger-width)] min-w-[280px] overflow-hidden",
            "rounded-lg border border-border bg-surface shadow-e3",
            "data-[state=open]:animate-in data-[state=closed]:animate-out",
            "data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95",
            "data-[side=bottom]:slide-in-from-top-1",
            "duration-180 motion-reduce:animate-none",
          )}
        >
          <div className="flex items-center gap-2 border-b border-border-soft px-3">
            <Search className="size-4 shrink-0 text-text-3" aria-hidden="true" />
            <input
              ref={inputRef}
              value={query}
              onChange={(e) => {
                setQuery(e.target.value)
              }}
              onKeyDown={onKeyDown}
              placeholder={searchPlaceholder}
              role="combobox"
              aria-autocomplete="list"
              aria-expanded={open}
              aria-controls={listId}
              aria-activedescendant={flat[active] ? `${listId}-${flat[active].value}` : undefined}
              className={cn(
                "h-9 min-w-0 flex-1 bg-transparent text-body text-text outline-none",
                "placeholder:text-text-disabled",
              )}
            />
            {loading && (
              <Loader2 className="size-3.5 shrink-0 animate-spin text-text-3 motion-reduce:animate-none" />
            )}
          </div>

          {scope && (
            <div className="flex items-center gap-2 border-b border-border-soft bg-surface-sunken px-3 py-2">
              <span className="text-caption text-text-2">
                Showing rules for <span className="font-id text-text">{scope.label}</span>
              </span>
              <span className="font-id text-micro tabular-nums text-text-3">{inScope.length}</span>
              <div className="flex-1" />
              <button
                type="button"
                onClick={onClearScope}
                className={cn(
                  "inline-flex items-center gap-1 rounded-xs px-1.5 py-0.5",
                  "text-caption font-medium text-brand",
                  "hover:bg-brand-tint",
                )}
              >
                <X className="size-3" aria-hidden="true" />
                Show all {options.length}
              </button>
            </div>
          )}

          <div ref={listRef} id={listId} role="listbox" className="max-h-72 overflow-y-auto p-1">
            {filtered.length === 0 ? (
              <div className="px-3 py-6 text-center">
                <p className="text-body-sm text-text-2">
                  {emptyMessage ?? `Nothing matches “${query}”`}
                </p>
                {scope && (
                  <button
                    type="button"
                    onClick={onClearScope}
                    className="mt-2 text-caption font-medium text-brand hover:underline"
                  >
                    Search all {options.length} instead
                  </button>
                )}
              </div>
            ) : (
              groups.map(([group, items]) => (
                <div key={group || "_"}>
                  {group && (
                    <div className="px-2 pb-1 pt-2.5 font-id text-eyebrow uppercase text-text-3">
                      {group}
                    </div>
                  )}
                  {items.map((o) => {
                    if (!o.disabled) index += 1
                    const i = index
                    const isActive = !o.disabled && i === active
                    const isSelected = o.value === value
                    return (
                      <div
                        key={o.value}
                        id={`${listId}-${o.value}`}
                        data-index={o.disabled ? undefined : i}
                        role="option"
                        aria-selected={isSelected}
                        aria-disabled={o.disabled || undefined}
                        onMouseEnter={() => !o.disabled && setActive(i)}
                        onClick={() => !o.disabled && commit(o)}
                        className={cn(
                          "flex cursor-pointer items-start gap-2.5 rounded-md px-2 py-1.5",
                          isActive && "bg-surface-sunken",
                          isSelected && "bg-brand-tint",
                          o.disabled && "cursor-not-allowed opacity-50",
                        )}
                      >
                        {o.icon && (
                          <span className="mt-0.5 shrink-0 [&_svg]:size-4" aria-hidden="true">
                            {o.icon}
                          </span>
                        )}
                        <span className="min-w-0 flex-1">
                          <span className="flex items-baseline gap-2">
                            <span className={cn("truncate text-body text-text", mono && "font-id")}>
                              {o.label}
                            </span>
                            {o.meta && (
                              <span className="ml-auto shrink-0 font-id text-micro text-text-3">
                                {o.meta}
                              </span>
                            )}
                          </span>
                          {o.description && (
                            <span className="mt-0.5 block text-caption leading-normal text-text-3">
                              {o.description}
                            </span>
                          )}
                        </span>
                        {isSelected && (
                          <Check
                            className="mt-0.5 size-3.5 shrink-0 text-brand"
                            aria-hidden="true"
                          />
                        )}
                      </div>
                    )
                  })}
                </div>
              ))
            )}
          </div>

          {footer && <div className="border-t border-border-soft p-1">{footer}</div>}
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  )
}
